import once from 'lodash/once'
import { Model } from 'infra/model'
import { observable, makeObservable, computed } from 'mobx'
import Job from './Job'
import { Id } from 'types/definitions'
import {
  JobScopeChangeRequestDTO,
  JobScopeChangeItemDTO,
  JobScopeChangeStatus,
} from './dtos/job-scope-change'
import { DateZero } from 'utils/date-util'

export default class JobScopeChangeRequest extends Model<
  JobScopeChangeRequestDTO,
  JobScopeChangeRequest
> {
  id = Id.none

  /**
   * Default scope change request.
   */
  static get none() {
    return lazyNone()
  }

  @observable reason = ''
  @observable status: JobScopeChangeStatus = 'PendingFinalization'
  @observable items: JobScopeChangeItemDTO[] = []
  @observable createTime: Date = DateZero()
  @observable rejectionReason = ''
  @observable createdByUserPublicId = Id.none

  constructor(public job: Job | undefined) {
    super()
    makeObservable(this)
  }

  get isAccepted() {
    return this.status === 'Accepted'
  }

  get isRejected() {
    return this.status === 'Rejected'
  }

  get isPending() {
    return this.status !== 'Accepted' && this.status !== 'Rejected'
  }

  @computed
  get isCreatedByAdmin() {
    const createdByCollaborator = this.job?.collaborators.find(
      (c) => c.member.user.publicId === this.createdByUserPublicId
    )
    return createdByCollaborator === undefined
  }

  parse(
    attrs: Partial<JobScopeChangeRequestDTO>
  ): Partial<JobScopeChangeRequest> {
    return {
      id: attrs.id,
      reason: attrs.reason,
      rejectionReason: attrs.rejectionReason,
      status: attrs.status,
      items: attrs.items,
      createTime: attrs.create_time,
      createdByUserPublicId: attrs.created_by_user_public_id,
    }
  }
}

const lazyNone = once(() =>
  new JobScopeChangeRequest(Job.none).set({
    status: 'PendingFinalization',
    items: [],
  })
)
